import i18n from "i18n";
import lod_ from "lodash";
import React, { useEffect, useRef } from "react";
import { Box, Divider, Typography, Paper, Chip } from "@material-ui/core";
import { handleSpecialDisplayDate } from "helpers/utilities";
import NewsAttachmentPreview from "./NewsAttachmentPreview";

/**
 * Get the news content based on the specified language
 * @param {object} content - The content object containing news in different languages
 * @param {string} language - The language code to fetch the news content for
 * @returns {object} - The news content in the specified language or the first available language
 */
const getNewsContent = (content, language) => {
	const newsContent = content[language.toUpperCase()] || content[language.toLowerCase()];
	if (lod_.isNil(newsContent)) {
		// Take the first available language if the specified one is not found
		const fallbackLanguage = Object.keys(content)[0];
		return content[fallbackLanguage];
	}
	return newsContent;
};

/**
 * NewsPost component to display individual news items
 * @param {object} props - The component props
 * @param {object} props.news - The news item to display
 * @param {string} props.language - The language code for the news content
 * @param {string} props.dateFormat - The format for displaying the date
 * @param {function} props.onLoad - Callback function to call when the component is loaded
 * @returns {JSX.Element} - The rendered news post component
 */
const ClientNewsPost = ({ news, language, dateFormat, onLoad, categories: dbCategories = [] }) => {
	const contentRef = useRef(null);
	const publishedDateTS = new Date(news.publishDate).getTime();
	const publishDate = handleSpecialDisplayDate(publishedDateTS, dateFormat, language);

	const newsContent = getNewsContent(news?.content?.[0] ?? {}, language);

	const subject = newsContent.subject;
	const htmlText = newsContent.text;

	const categories = news.categories ?? [];

	const attachments = !lod_.isNil(newsContent.attachments)
		? newsContent.attachments.filter(att => att.type !== "inline")
		: [];

	useEffect(() => {
		if (!onLoad) {
			return;
		}

		let isMounted = true;

		const safeMeasure = () => {
			if (isMounted === true) {
				onLoad();
			}
		};

		safeMeasure();

		//GET images inside the content of news post
		const images = contentRef.current?.querySelectorAll("img");

		if (!images || images.length === 0) {
			return () => {
				isMounted = false;
			};
		}

		let loadedCount = 0;
		const handleImageLoad = () => {
			loadedCount++;
			if (loadedCount === images.length) {
				safeMeasure();
			}
		};

		// ADD event listeners to images
		images.forEach(img => {
			if (img.complete) {
				loadedCount++;
			} else {
				img.addEventListener("load", handleImageLoad);
				img.addEventListener("error", handleImageLoad);
			}
		});

		// If all images are already loaded
		if (loadedCount === images.length) {
			safeMeasure();
		}

		// Cleanup
		return () => {
			isMounted = false;
			images.forEach(img => {
				img.removeEventListener("load", handleImageLoad);
				img.removeEventListener("error", handleImageLoad);
			});
		};
	}, [onLoad]);

	return (
		<Paper
			elevation={2}
			style={{
				padding: 16,
				width: "80%",
				marginBottom: 16,
				boxSizing: "border-box"
			}}
		>
			{/* Header */}
			<Box display="flex" justifyContent="space-between" alignItems="flex-start" marginBottom={2}>
				<Box flex={1}>
					<Typography
						variant="h6"
						style={{ fontWeight: "bold" }}
						dangerouslySetInnerHTML={{ __html: subject }}
					></Typography>
					<Typography variant="caption" color="textSecondary">
						{publishDate}
					</Typography>
				</Box>
			</Box>

			{/* Categories */}
			{categories.length > 0 && (
				<Box display="flex" flexWrap="wrap" sx={{ mb: 2 }}>
					{categories.map((category, index) => {
						const possibleCategoryID = category.answerItemCategoryId;
						if (!lod_.isNil(possibleCategoryID)) {
							const dbCategory = dbCategories.find(cat => cat._id === possibleCategoryID);
							if (!lod_.isNil(dbCategory)) {
								category = dbCategory;
							}
						}

						return (
							<Box key={index} sx={{ mr: 1, mb: 1 }}>
								<Chip
									label={category.name}
									variant="outlined"
									style={{
										color: category.color,
										borderColor: category.color,
										fontWeight: "bold",
										backgroundColor: "transparent"
									}}
									size="small"
								/>
							</Box>
						);
					})}
				</Box>
			)}

			<Divider style={{ margin: "8px 0" }} />

			{/* Attachments */}
			{attachments.length > 0 && (
				<Typography variant="caption" color="textSecondary" style={{ marginBottom: 8 }}>
					{i18n.t("COC.Attachements")}
				</Typography>
			)}
			<Box
				style={{
					display: "flex",
					flexDirection: "row",
					gap: "5px"
				}}
			>
				{attachments.map((attachment, index) => {
					return (
						<NewsAttachmentPreview
							key={index}
							attachement={attachment}
							style={{
								height: "10vh",
								width: "10vw"
							}}
							actions={["viewFile", "downloadFile"]}
						/>
					);
				})}
			</Box>

			{/* Body */}
			<Box ref={contentRef} sx={{ mt: 1 }} dangerouslySetInnerHTML={{ __html: htmlText }}></Box>
		</Paper>
	);
};

export default ClientNewsPost;
